import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { postMessage } from "../utils/helper";
import { ReactComponent as Upload } from "../assets/icons/Upload.svg";
import { ReactComponent as Camera } from "../assets/icons/camera.svg";
import { ReactComponent as Close } from "../assets/icons/Close.svg";
import DocAnalyzing from "../assets/images/DocAnalyzing.png";

const UploadDocuments = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [picture, setPicture] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const inputRef = useRef(null);

  const selectPicture = (file) => {
    if (file) {
      setPictureFile(file);
      const reader = new FileReader();
      reader.onloadend = (e) => setPicture(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPicture(null);
      setPictureFile(null);
      if (inputRef.current) inputRef.current.value = null;
    }
  };

  const openCamera = () => {
    if (inputRef.current) {
      inputRef.current.setAttribute("capture", "environment");
      inputRef.current.click();
    }
  };

  const openGallery = () => {
    if (inputRef.current) {
      inputRef.current.removeAttribute("capture");
      inputRef.current.click();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (!picture || !pictureFile) throw new Error("Please select an image.");
      if (pictureFile.size > 10485760) throw new Error("Please select a valid image (<10MB).");
      const { url, fields } = state?.presignedUrl || {};
      if (!url || !fields) throw new Error("Upload configuration is missing.");
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", pictureFile);
      await fetch(url, { method: "POST", body: formData, mode: "no-cors" });
      navigate("/scan-instructions", { state: { ...state }, replace: true });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Upload failed. Please try again.");
      postMessage("error", err?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <section className="px-6 pt-24 pb-8">
      <h4 className="my-8 text-xl font-semibold text-white">Upload Your Identity Document</h4>

      <form
        onSubmit={submit}
        onReset={() => {
          setError("");
          selectPicture(null);
        }}>
        <div className="mt-24 space-y-3">
          <input
            ref={inputRef}
            className="sr-only"
            type="file"
            id="image"
            name="image"
            accept=".jpg, .jpeg, .png"
            onChange={(e) => selectPicture(e.target?.files?.[0])}
            disabled={loading}
          />
          {picture ? (
            <div className="h-40 max-w-max relative">
              <img className="h-full object-fill rounded-xl" src={picture} alt="preview" />
              <button
                className="absolute -top-3 -right-3 h-6 w-6 p-0.5 rounded-full bg-error text-white"
                type="button"
                onClick={() => selectPicture(null)}
                disabled={loading}>
                <Close className="h-full w-full" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div
                  onClick={openGallery}
                  className="h-40 flex flex-col justify-center items-center rounded-xl cursor-pointer bg-gradient-to-b from-[#680FAD] to-[#063BC4]">
                  <Upload className="h-12 w-12 mb-2" />
                  <p className="text-xs">Upload Image</p>
                </div>
                <div
                  onClick={openCamera}
                  className="h-40 flex flex-col justify-center items-center rounded-xl cursor-pointer bg-gradient-to-b from-[#680FAD] to-[#063BC4]">
                  <Camera className="h-11 w-11 mb-1" />
                  <p className="text-xs mt-2">Take Photo</p>
                </div>
              </div>
              <p className="text-xxs text-center opacity-70">Only .jpg / .jpeg / .png files (max 10MB)</p>
            </div>
          )}
        </div>

        {loading && (
          <div className="fixed inset-0 flex flex-col items-center justify-center bg-primary/95 text-white text-center">
            <img className="h-48" src={DocAnalyzing} alt="Analyzing..." />
            <h4 className="mt-6 text-lg font-semibold text-ternary">Uploading Image</h4>
          </div>
        )}

        {error && <p className="mt-8 text-error text-xs text-center">{error}</p>}

        <button
          type="submit"
          disabled={loading || !picture}
          className="block mt-8 w-full rounded-full outline-none px-5 py-2.5 bg-gradient disabled:bg-none disabled:bg-[#9EAAC7] text-white">
          Next
        </button>
      </form>
    </section>
  );
};

export default UploadDocuments;
